/**
 * Vector zoom / pan controller for an injected SVG drawing.
 *
 * Zooming is done by animating the SVG `viewBox` rather than a CSS transform, so
 * geometry stays vector-crisp at every level and stroke widths keep their CAD-like
 * proportion instead of ballooning.
 */

import { mainContentBox, elementBoxes } from './drawing-extent.js';

const MIN_ZOOM = 0.01; // 1%  of the fit-to-window scale
const MAX_ZOOM = 500; // 50000% of the fit-to-window scale
const FIT_PADDING = 1.06; // a little breathing room around the drawing

export class SvgViewer {
  /** @param {HTMLElement} host element that owns the <svg> */
  constructor(host) {
    this.host = host;
    this.svg = null;
    this.base = null; // viewBox that represents "fit"
    this.vb = null; // live viewBox
    this.fitScale = 1; // px per drawing unit at fit
    this._pointers = new Map();
    this._pinch = null;
    this._drag = null;
    this.onViewChange = null; // callback({ zoom })
    this.onCursor = null; // callback({ x, y } | null)

    this._bind();
  }

  /* ---------------------------------------------------------------- content */

  /**
   * @param {string} svgString raw SVG produced by LibreDWG
   * @param {{handleToLayer?: Array<[string,string]>}} [options]
   */
  setContent(svgString, options = {}) {
    this.clear();

    const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
    const error = doc.querySelector('parsererror');
    if (error) throw new Error('SVG 解析失敗：' + error.textContent.slice(0, 120));

    const svg = doc.documentElement;
    if (!svg || svg.tagName.toLowerCase() !== 'svg') throw new Error('SVG 根節點遺失');

    // Own the viewport: fill the host, centre the drawing, no baked-in size.
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.classList.add('dwg-svg');

    const handleToLayer = new Map(options.handleToLayer ?? []);
    if (handleToLayer.size) {
      for (const g of svg.querySelectorAll('g[id]')) {
        const layer = handleToLayer.get(g.id);
        if (layer) g.dataset.layer = layer;
      }
    }

    this.svg = document.importNode(svg, true);
    this.host.appendChild(this.svg);

    const vb = readViewBox(this.svg) ?? computeGeometryViewBox(this.svg);
    if (!vb) {
      throw new Error(
        '此圖面沒有可縮放的幾何範圍——SVG 缺少 viewBox，且找不到任何可繪製的實體' +
          '（模型空間可能是空的，或內容都在佈局/紙空間）。可改用 v2（WebGL 版）再試。'
      );
    }

    // Normalise the fit box with padding so nothing touches the viewport edge.
    const cx = vb.x + vb.w / 2;
    const cy = vb.y + vb.h / 2;
    const w = vb.w * FIT_PADDING || 1;
    const h = vb.h * FIT_PADDING || 1;
    this.base = { x: cx - w / 2, y: cy - h / 2, w, h };
    this.fit(true);
  }

  clear() {
    if (this.svg) {
      this.svg.remove();
      this.svg = null;
    }
    this.base = null;
    this.vb = null;
    this._content = undefined;
  }

  get hasContent() {
    return Boolean(this.svg && this.vb);
  }

  /* ------------------------------------------------------------- transforms */

  fit(instant = false) {
    if (!this.base) return;
    this.vb = { ...this.base };
    this.fitScale = this._scale() || this.fitScale; // px per drawing unit at fit
    this._apply(instant);
  }

  /** Fit the main cluster of content, ignoring far-away stray objects (see drawing-extent.js). */
  fitContent(instant = false) {
    if (!this.base) return;
    if (this._content === undefined) {
      const rect = this.host.getBoundingClientRect();
      if (!rect.width || !rect.height) { this.fit(instant); return; }
      this._content = mainContentBox(elementBoxes(this.svg), this.base);
    }
    if (!this._content) { this.fit(instant); return; }
    this.vb = { ...this._content };
    this._apply(instant);
  }

  /** True when the main-content view differs from the full extent. */
  get hasContentFit() {
    return Boolean(this._content);
  }

  zoomTo(pct) {
    if (!this.hasContent) return;
    const rect = this.host.getBoundingClientRect();
    this._zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, pct / this.zoom());
  }

  /** Set the view so that one drawing unit equals one screen pixel. */
  zoomToActual() {
    if (!this.hasContent) return;
    const rect = this.host.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const target = this.fitScale ? (1 / this.fitScale) : 1; // zoom multiple for scale == 1
    if (target > MAX_ZOOM) {
      // A full 1:1 view is far out of range; just clamp to the max instead.
      this.zoomTo(MAX_ZOOM * 100);
      return;
    }
    const cx = this.vb.x + this.vb.w / 2;
    const cy = this.vb.y + this.vb.h / 2;
    this.vb.w = rect.width / 1;
    this.vb.h = rect.height / 1;
    this.vb.x = cx - this.vb.w / 2;
    this.vb.y = cy - this.vb.h / 2;
    this._apply();
  }

  zoomBy(factor) {
    if (!this.hasContent) return;
    const rect = this.host.getBoundingClientRect();
    this._zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
  }

  /** Current zoom as a multiple of the fit-to-window scale (1 === 100%). */
  zoom() {
    if (!this.vb) return 1;
    const rect = this.host.getBoundingClientRect();
    const scale = this._scale(rect);
    return this.fitScale ? scale / this.fitScale : 1;
  }

  panBy(dxPx, dyPx) {
    if (!this.hasContent) return;
    const rect = this.host.getBoundingClientRect();
    const scale = this._scale(rect);
    if (!scale) return;
    this.vb.x -= dxPx / scale;
    this.vb.y -= dyPx / scale;
    this._apply(true);
  }

  /** Convert a client-space point to drawing units (accounts for letterboxing). */
  toDrawingPoint(clientX, clientY) {
    const rect = this.host.getBoundingClientRect();
    const scale = this._scale(rect);
    const rw = this.vb.w * scale;
    const rh = this.vb.h * scale;
    return {
      x: this.vb.x + (clientX - rect.left - (rect.width - rw) / 2) / scale,
      y: this.vb.y + (clientY - rect.top - (rect.height - rh) / 2) / scale,
    };
  }

  _scale(rect = this.host.getBoundingClientRect()) {
    if (!this.vb) return 0;
    return Math.min(rect.width / this.vb.w, rect.height / this.vb.h);
  }

  _zoomAt(clientX, clientY, factor) {
    if (!this.hasContent || !Number.isFinite(factor) || factor === 1) return;

    const clamped = clamp(factor, MIN_ZOOM / this.zoom(), MAX_ZOOM / this.zoom());
    if (!Number.isFinite(clamped) || clamped === 1) return;

    const before = this.toDrawingPoint(clientX, clientY);
    this.vb.w /= clamped;
    this.vb.h /= clamped;

    // Re-anchor so the point under the cursor does not move.
    const rect = this.host.getBoundingClientRect();
    const scale = this._scale(rect);
    const rw = this.vb.w * scale;
    const rh = this.vb.h * scale;
    this.vb.x = before.x - (clientX - rect.left - (rect.width - rw) / 2) / scale;
    this.vb.y = before.y - (clientY - rect.top - (rect.height - rh) / 2) / scale;
    this._apply(true);
  }

  _apply() {
    if (!this.svg || !this.vb) return;
    this.svg.setAttribute(
      'viewBox',
      `${round(this.vb.x)} ${round(this.vb.y)} ${round(this.vb.w)} ${round(this.vb.h)}`
    );
    this.onViewChange?.({ zoom: this.zoom() });
  }

  /* ----------------------------------------------------------- interaction */

  _bind() {
    const host = this.host;

    host.addEventListener(
      'wheel',
      (e) => {
        if (!this.hasContent) return;
        e.preventDefault();
        // Exponential in wheel delta so trackpads and mice feel the same.
        const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0016));
        this._zoomAt(e.clientX, e.clientY, factor);
      },
      { passive: false }
    );

    host.addEventListener('pointerdown', (e) => {
      if (!this.hasContent) return;
      host.setPointerCapture?.(e.pointerId);
      this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this._pointers.size === 2) {
        const [a, b] = [...this._pointers.values()];
        this._drag = null;
        this._pinch = {
          dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
          x: (a.x + b.x) / 2,
          y: (a.y + b.y) / 2,
        };
      } else if (this._pointers.size === 1) {
        this._drag = { x: e.clientX, y: e.clientY };
      }
    });

    host.addEventListener('pointermove', (e) => {
      if (!this.hasContent) return;

      if (this.onCursor) {
        const p = this.toDrawingPoint(e.clientX, e.clientY);
        this.onCursor(p);
      }

      if (!this._pointers.has(e.pointerId)) return;
      this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this._pinch && this._pointers.size >= 2) {
        const [a, b] = [...this._pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        this._zoomAt(mid.x, mid.y, dist / this._pinch.dist);
        this.panBy(mid.x - this._pinch.x, mid.y - this._pinch.y);
        this._pinch = { dist, x: mid.x, y: mid.y };
      } else if (this._drag) {
        this.panBy(e.clientX - this._drag.x, e.clientY - this._drag.y);
        this._drag = { x: e.clientX, y: e.clientY };
      }
    });

    const release = (e) => {
      this._pointers.delete(e.pointerId);
      if (this._pointers.size < 2) this._pinch = null;
      if (this._pointers.size === 0) this._drag = null;
    };
    host.addEventListener('pointerup', release);
    host.addEventListener('pointercancel', release);
    host.addEventListener('pointerleave', () => this.onCursor?.(null));

    host.addEventListener('dblclick', (e) => {
      if (!this.hasContent) return;
      e.preventDefault();
      this._zoomAt(e.clientX, e.clientY, e.shiftKey ? 1 / 1.6 : 1.6);
    });
  }

  destroy() {
    this.clear();
    this._pointers.clear();
    this._drag = null;
    this._pinch = null;
  }
}

function readViewBox(svg) {
  const raw = svg.getAttribute('viewBox');
  if (!raw) return null;
  const p = raw.trim().split(/[\s,]+/).map(Number);
  if (p.length !== 4 || !p.every((n) => Number.isFinite(n))) return null;
  const [x, y, w, h] = p;
  if (w <= 0 || h <= 0) return null;
  return { x, y, w, h };
}

/**
 * Fallback "fit" box: where LibreDWG's SVG has no usable root viewBox (empty
 * model space, degenerate/zero-area extents, paper-space-only content, …),
 * derive a zoomable window from the content. Three tiers:
 *  1. svg.getBBox() on the ROOT — exact, in root space with every transform
 *     applied (LibreDWG flips Y and places blocks via <use transform=…>);
 *     only works while the SVG is rendered;
 *  2. per-element getBBox() — rendered but transform-naive;
 *  3. per-element attribute estimate (line/polyline/rect/circle/path numbers)
 *     — works even when the host is hidden, so a fit window exists before
 *     first paint.
 */
function computeGeometryViewBox(svg) {
  // Tier 1: whole-content bbox (transform-aware).
  try {
    const b = svg.getBBox();
    if (
      b &&
      [b.x, b.y, b.width, b.height].every(Number.isFinite) &&
      (b.width > 0 || b.height > 0 || b.x !== 0 || b.y !== 0)
    ) {
      return { x: b.x, y: b.y, w: b.width, h: b.height };
    }
  } catch {
    /* not rendered — fall through */
  }

  const els = svg.querySelectorAll(
    'path, line, polyline, polygon, circle, ellipse, rect, text, image, use'
  );
  if (!els.length) return null;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  let any = false;
  for (const el of els) {
    let b = null;
    try {
      const gb = el.getBBox(); // tier 2
      if (gb && (gb.width > 0 || gb.height > 0 || gb.x !== 0 || gb.y !== 0)) b = gb;
    } catch {
      /* not rendered */
    }
    if (!b) b = attrBBox(el); // tier 3
    if (!b) continue;
    any = true;
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }
  if (!any || !Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  let w = maxX - minX;
  let h = maxY - minY;
  // Degenerate extents (a single line/point) still need a zoomable window.
  if (!Number.isFinite(w) || w <= 0) {
    w = 1;
    minX -= 0.5;
  }
  if (!Number.isFinite(h) || h <= 0) {
    h = 1;
    minY -= 0.5;
  }
  // Give near-zero extents some room so a point/line is actually visible.
  if (w < 1) {
    const pad = (1 - w) / 2;
    minX -= pad;
    w = 1;
  }
  if (h < 1) {
    const pad = (1 - h) / 2;
    minY -= pad;
    h = 1;
  }
  return { x: minX, y: minY, w, h };
}

/** Rough per-element bbox from presentation attributes (no rendering needed). */
function attrBBox(el) {
  const t = el.tagName.toLowerCase();
  const num = (s) => {
    const n = s == null ? NaN : parseFloat(s);
    return Number.isFinite(n) ? n : null;
  };
  const g = (name) => num(el.getAttribute(name));
  const pts = [];
  const push = (x, y) => {
    if (x != null && y != null) pts.push(x, y);
  };
  if (t === 'line') {
    push(g('x1'), g('y1'));
    push(g('x2'), g('y2'));
  } else if (t === 'polyline' || t === 'polygon') {
    const list = (el.getAttribute('points') || '').trim().split(/[\s,]+/);
    for (let i = 0; i + 1 < list.length; i += 2) push(num(list[i]), num(list[i + 1]));
  } else if (t === 'rect') {
    const x = g('x') ?? 0;
    const y = g('y') ?? 0;
    const w = g('width');
    const h = g('height');
    if (w != null && h != null) {
      push(x, y);
      push(x + w, y + h);
    }
  } else if (t === 'circle' || t === 'ellipse') {
    const cx = g('cx') ?? 0;
    const cy = g('cy') ?? 0;
    const r = g(t === 'circle' ? 'r' : 'rx');
    const s = g(t === 'circle' ? 'r' : 'ry');
    if (r != null && s != null) {
      push(cx - r, cy - s);
      push(cx + r, cy + s);
    }
  } else if (t === 'image' || t === 'use') {
    const x = g('x') ?? 0;
    const y = g('y') ?? 0;
    const w = g('width');
    const h = g('height');
    if (w != null && h != null && (w > 0 || h > 0)) {
      push(x, y);
      push(x + w, y + h);
    }
  } else if (t === 'path') {
    const nums = (el.getAttribute('d') || '').match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) ?? [];
    for (let i = 0; i + 1 < nums.length; i += 2) push(num(nums[i]), num(nums[i + 1]));
  } else if (t === 'text') {
    push(g('x') ?? 0, g('y') ?? 0);
  }
  if (pts.length < 2) return null;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (let i = 0; i < pts.length; i += 2) {
    if (pts[i] < minX) minX = pts[i];
    if (pts[i] > maxX) maxX = pts[i];
    if (pts[i + 1] < minY) minY = pts[i + 1];
    if (pts[i + 1] > maxY) maxY = pts[i + 1];
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function clamp(v, lo, hi) {
  return Math.min(Math.max(v, lo), hi);
}

function round(n) {
  return Math.round(n * 1e6) / 1e6;
}
